/**
 * AIDisclosure – Wiederverwendbare Transparenzhinweise gemaess EU AI Act (Art. 50).
 *
 * Varianten:
 *   - <AIBadge />          – kompaktes "KI"-Pill fuer Header, Buttons
 *   - <AINotice />         – schmale Info-Zeile mit Link zur Datenschutzerklaerung
 *   - <AIFirstInteraction />  – ausfuehrlicher Erst-Interaktions-Hinweis (Bot-Bubble)
 *
 * Konfiguration: /app/frontend/src/config/compliance.js
 */
import { Sparkles, Info } from 'lucide-react';
import { AI_DISCLOSURE, AI_DISCLOSURE_ENABLED } from '@/config/compliance';

/** Kompaktes "KI"-Pill – fuer Chatbot-Header, Buttons, Chip-Labels */
export const AIBadge = ({ className = '', variant = 'dark' }) => {
  if (!AI_DISCLOSURE_ENABLED) return null;
  const styles =
    variant === 'dark'
      ? 'bg-[#D4AA6A]/25 text-[#F5D9AC] border-[#D4AA6A]/50'
      : 'bg-[#4A331D]/10 text-[#4A331D] border-[#4A331D]/25';
  return (
    <span
      data-testid="ai-badge"
      title={AI_DISCLOSURE.legalBasis}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider border ${styles} ${className}`}
    >
      <Sparkles className="w-2.5 h-2.5" strokeWidth={2.5} />
      {AI_DISCLOSURE.shortLabel}
    </span>
  );
};

/** Schmale Info-Zeile – z.B. unter dem Chat-Header, mit Datenschutz-Link */
export const AINotice = ({ className = '' }) => {
  if (!AI_DISCLOSURE_ENABLED) return null;
  return (
    <div
      data-testid="ai-notice"
      className={`px-3 py-1.5 bg-[#FFF8EC] border-b border-[#D4AA6A]/30 flex items-start gap-1.5 text-[10.5px] leading-snug text-[#5b4225] ${className}`}
    >
      <Info className="w-3 h-3 mt-[1px] flex-shrink-0 text-[#B8894A]" strokeWidth={2.2} />
      <span>
        {AI_DISCLOSURE.inlineNotice}{' '}
        <a
          href={AI_DISCLOSURE.privacyLink}
          target="_blank"
          rel="noreferrer"
          className="underline font-medium hover:text-[#4A331D]"
          data-testid="ai-notice-privacy-link"
        >
          {AI_DISCLOSURE.privacyLinkLabel}
        </a>
      </span>
    </div>
  );
};

/** Erst-Interaktions-Hinweis – erscheint als System-Bubble beim Chat-Start */
export const AIFirstInteraction = () => {
  if (!AI_DISCLOSURE_ENABLED) return null;
  return (
    <div
      data-testid="ai-first-interaction"
      className="flex justify-start animate-[fadeIn_0.25s_ease-out]"
    >
      <div className="max-w-[85%] px-3 py-2 rounded-2xl bg-[#FFF8EC] border border-[#D4AA6A]/40 text-[11.5px] leading-relaxed text-[#5b4225] flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 mt-[1px] flex-shrink-0 text-[#B8894A]" strokeWidth={2.2} />
        <span>
          {AI_DISCLOSURE.firstInteractionNotice}{' '}
          <a
            href={AI_DISCLOSURE.privacyLink}
            target="_blank"
            rel="noreferrer"
            className="underline font-medium hover:text-[#4A331D]"
          >
            {AI_DISCLOSURE.privacyLinkLabel}
          </a>
        </span>
      </div>
    </div>
  );
};

export default { AIBadge, AINotice, AIFirstInteraction };
